import { Component } from '@theme/component';
import { morphSection } from '@theme/section-renderer';
import { fetchFilteredRelatedProducts, isProductAvailable } from '@theme/product-main-collection-filter';

const RECOMMENDATION_CARD_SECTION_ID = 'product-recommendation-card';

class ProductRecommendations extends Component {
  /**
   * The observer for the product recommendations
   * @type {IntersectionObserver}
   */
  #intersectionObserver = new IntersectionObserver(
    (entries, observer) => {
      if (!entries[0]?.isIntersecting) return;

      observer.disconnect();
      this.#loadRecommendations();
    },
    { rootMargin: '0px 0px 400px 0px' }
  );

  /**
   * Observing changes to the elements attributes
   * @type {MutationObserver}
   */
  #mutationObserver = new MutationObserver((mutations) => {
    for (const mutation of mutations) {
      if (mutation.target !== this || mutation.type !== 'attributes') continue;
      if (mutation.attributeName === 'data-error') continue;
      if (mutation.attributeName === 'class' && this.classList.contains('hidden')) continue;

      if (
        mutation.attributeName === 'data-recommendations-performed' &&
        this.dataset.recommendationsPerformed === 'true'
      ) {
        continue;
      }

      this.#loadRecommendations();
      break;
    }
  });

  /**
   * The cached recommendations
   * @type {Record<string, string>}
   */
  #cachedRecommendations = {};

  /**
   * @type {Record<string, HTMLElement>}
   */
  #cachedRecommendationCards = {};

  /**
   * An abort controller for the active fetch (if there is one)
   * @type {AbortController | null}
   */
  #activeFetch = null;

  connectedCallback() {
    super.connectedCallback();
    this.#intersectionObserver.observe(this);
    this.#mutationObserver.observe(this, { attributes: true });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#intersectionObserver.disconnect();
    this.#mutationObserver.disconnect();
  }

  /**
   * Load the product recommendations
   */
  #loadRecommendations() {
    const { productId, recommendationsPerformed, sectionId, intent } = this.dataset;

    if (!productId || !sectionId) {
      throw new Error('Product ID and a section ID are required');
    }

    if (recommendationsPerformed === 'true') {
      return;
    }

    const limit = parseInt(this.dataset.maxProducts || '4', 10);
    const mainCollectionHandle = this.dataset.mainCollection || '';
    const recommendationsUrl = Theme?.routes?.product_recommendations_url || '/recommendations/products';

    Promise.all([
      fetchFilteredRelatedProducts(productId, limit, mainCollectionHandle, recommendationsUrl),
      this.#fetchCachedRecommendations(productId, sectionId, intent),
    ])
      .then(async ([filteredProducts, result]) => {
        if (!result.success) {
          if (!Shopify.designMode) {
            this.#handleError(new Error(`Server returned ${result.status}`));
          }
          return;
        }

        if (!filteredProducts.length) {
          this.#handleError(new Error('No recommendations available'));
          return;
        }

        if (result.data?.trim().length) {
          this.dataset.recommendationsPerformed = 'true';
          morphSection(sectionId, result.data, { mode: 'hydration', injectStylesheet: true });
        } else {
          this.dataset.recommendationsPerformed = 'true';
        }

        await this.#applyFilteredProducts(sectionId, filteredProducts);
      })
      .catch((error) => {
        this.#handleError(error);
      });
  }

  /**
   * @param {string} sectionId
   * @param {any[]} filteredProducts
   */
  async #applyFilteredProducts(sectionId, filteredProducts) {
    const section = document.getElementById(`shopify-section-${sectionId}`);
    if (!section) return;

    const lists = section.querySelectorAll('.resource-list');
    if (!lists.length) {
      this.#handleError(new Error('No recommendations available'));
      return;
    }

    for (const list of lists) {
      await this.#filterResourceList(list, filteredProducts);
    }

    const hasVisibleItems = [...lists].some((list) => list.querySelector('.resource-list__item'));
    if (!hasVisibleItems) {
      this.#handleError(new Error('No recommendations available'));
      return;
    }

    section.querySelectorAll('[data-has-recommendations]').forEach((element) => {
      element.setAttribute('data-has-recommendations', 'true');
    });
  }

  /**
   * @param {Element} list
   * @param {any[]} filteredProducts
   */
  async #filterResourceList(list, filteredProducts) {
    const items = [...list.querySelectorAll('.resource-list__item')];
    const itemsById = new Map();

    for (const item of items) {
      const productId = this.#getProductIdFromListItem(item);
      if (productId) {
        itemsById.set(productId, item);
      }
    }

    items.forEach((item) => item.remove());

    for (const product of filteredProducts) {
      if (!isProductAvailable(product)) continue;

      const productId = Number(product.id);
      let item = itemsById.get(productId);

      if (!item && product.handle) {
        item = await this.#fetchRecommendationListItem(product.handle);
      }

      if (item) {
        list.appendChild(item);
      }
    }
  }

  /**
   * @param {Element} item
   * @returns {number | null}
   */
  #getProductIdFromListItem(item) {
    const explicitId = item.getAttribute('data-product-id');
    if (explicitId) return Number(explicitId);

    const nested = item.querySelector('[data-product-id]');
    if (nested?.dataset.productId) return Number(nested.dataset.productId);

    return null;
  }

  /**
   * @param {string} handle
   * @returns {Promise<HTMLElement | null>}
   */
  async #fetchRecommendationListItem(handle) {
    if (this.#cachedRecommendationCards[handle]) {
      return this.#cachedRecommendationCards[handle].cloneNode(true);
    }

    const url = new URL(`/products/${encodeURIComponent(handle)}`, window.location.origin);
    url.searchParams.set('sections', RECOMMENDATION_CARD_SECTION_ID);

    const response = await fetch(url.toString());
    if (!response.ok) return null;

    const data = await response.json();
    const html = data?.[RECOMMENDATION_CARD_SECTION_ID];
    if (!html) return null;

    const doc = new DOMParser().parseFromString(html, 'text/html');
    const item = doc.querySelector('.resource-list__item');
    if (!(item instanceof HTMLElement)) return null;

    this.#cachedRecommendationCards[handle] = item;
    return item.cloneNode(true);
  }

  /**
   * Fetches the recommendations and cached the result for future use
   * @param {string} productId
   * @param {string | undefined} sectionId
   * @param {string | undefined} intent
   * @returns {Promise<{ success: true, data: string } | { success: false, status: number }>}
   */
  async #fetchCachedRecommendations(productId, sectionId, intent) {
    const url = `${this.dataset.url}&product_id=${productId}&section_id=${sectionId}&intent=${intent}`;

    const cachedResponse = this.#cachedRecommendations[url];
    if (cachedResponse) {
      return { success: true, data: cachedResponse };
    }

    this.#activeFetch?.abort();
    this.#activeFetch = new AbortController();

    try {
      const response = await fetch(url, { signal: this.#activeFetch.signal });
      if (!response.ok) {
        return { success: false, status: response.status };
      }

      const text = await response.text();
      this.#cachedRecommendations[url] = text;
      return { success: true, data: text };
    } finally {
      this.#activeFetch = null;
    }
  }

  /**
   * Handle errors in a consistent way
   * @param {Error} error
   */
  #handleError(error) {
    console.error('Product recommendations error:', error.message);
    this.classList.add('hidden');
    this.dataset.error = 'Error loading product recommendations';
  }
}

if (!customElements.get('product-recommendations')) {
  customElements.define('product-recommendations', ProductRecommendations);
}
